import { z } from 'zod';

/**
 * Intake configuration — what a tenant admin tunes about how the intake
 * assistant treats this desk's mail (INTAKE-MIGRATION-PLAN.md, "Who owns
 * what").
 *
 * THE PROMPT IS ASSEMBLED, NOT EDITED. The assistant's prompt is a platform
 * base — tool protocol, reply versus note, never narrating its process,
 * "judge what is being asked for, not what is named" — plus the sections
 * rendered from this. A tenant admin tuning tone cannot delete the rule that
 * keeps internal notes out of the requester's inbox, because that rule is not
 * in anything they can edit. Hence bounded fields with their own limits, not
 * one textbox over the whole prompt.
 *
 * STRUCTURED SCOPE. `handles` / `doesNotHandle` / `examples` rather than a
 * paragraph, because one field then serves two readers: this desk's own
 * "is this mine?" check, and — if the shelved front-door router is ever
 * built — a router asking every desk's scope "whose is this?".
 *
 * Lives in @esa/shared so the screen, the API and (from stage 3) the agent
 * read the same schema and the same defaults.
 */

// ---------------------------------------------------------------------------
// Personal data — what the assistant may file a ticket with, per tenant
// ---------------------------------------------------------------------------

/**
 * The kinds of personal data the deterministic check recognises. Named as the
 * n8n guardrail names them, so a desk's settings can be compared with what
 * the n8n desk blocks today. The n8n list's UK identifiers (NHS, National
 * Insurance) are left out: ESA is not international.
 *
 * THE DEFAULT IS NONE. A message carrying any of these is declined with the
 * personal-data wording and no ticket is opened — what the n8n desk does. A
 * tenant admin then allows, per desk, only what that desk's real work needs:
 * Payroll bank details for a direct-deposit change, say. It is a checklist
 * rather than a set of named policies because the right answer differs desk
 * by desk and type by type, and a named policy is always slightly wrong for
 * somebody.
 */
export const PII_ENTITIES = [
  'CREDIT_CARD',
  'CRYPTO',
  'IBAN_CODE',
  'US_BANK_NUMBER',
  'US_SSN',
  'US_ITIN',
  'US_PASSPORT',
  'US_DRIVER_LICENSE',
  'MEDICAL_LICENSE',
  'PASSWORD',
] as const;
export type PiiEntity = (typeof PII_ENTITIES)[number];

export interface PiiEntityInfo {
  label: string;
  /** What it looks like, so an admin knows what they are allowing. */
  example: string;
  /**
   * False when no desk may allow it:
   *   - card numbers: holding them brings a system into card-industry (PCI)
   *     scope, and no service desk has a reason to receive one;
   *   - passwords: a ticket is read by several people and kept, and a
   *     password in one is a password shared. The desk never needs it — a
   *     reset does not start from the old one.
   */
  allowable: boolean;
}

/** How each is named in a sentence: "this message contains a password". */
export const PII_PHRASE: Record<PiiEntity, string> = {
  CREDIT_CARD: 'a payment card number',
  CRYPTO: 'a cryptocurrency wallet address',
  IBAN_CODE: 'a bank account (IBAN) number',
  US_BANK_NUMBER: 'a bank account number',
  US_SSN: 'a Social Security number',
  US_ITIN: 'a taxpayer ID (ITIN)',
  US_PASSPORT: 'a passport number',
  US_DRIVER_LICENSE: "a driver's licence number",
  MEDICAL_LICENSE: 'a medical licence number',
  PASSWORD: 'a password',
};

/**
 * A personal-data decline with what was found named in it.
 *
 * `{found}` becomes "a password" or "a Social Security number and a bank
 * account number". A password also gets the one sentence that matters most
 * — added whatever the wording, because it is advice, not phrasing.
 */
export function fillPersonalDataDecline(text: string, found: readonly string[]): string {
  const phrases = found.map((f) => PII_PHRASE[f as PiiEntity] ?? 'personal details');
  const list =
    phrases.length === 0
      ? 'personal details'
      : phrases.length === 1
        ? phrases[0]!
        : `${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}`;
  let out = text.split('{found}').join(list);
  if (found.includes('PASSWORD')) {
    out += '\n\nNobody at the desk will ever need your password. If you have sent it to anyone, change it now.';
  }
  return out;
}

export const PII_INFO: Record<PiiEntity, PiiEntityInfo> = {
  CREDIT_CARD: { label: 'Payment card numbers', example: '4111 1111 1111 1111', allowable: false },
  CRYPTO: { label: 'Cryptocurrency wallet addresses', example: 'bc1q…', allowable: true },
  IBAN_CODE: { label: 'IBAN bank account numbers', example: 'GB29 NWBK 6016 1331 9268 19', allowable: true },
  US_BANK_NUMBER: { label: 'US bank account numbers', example: 'routing and account numbers', allowable: true },
  US_SSN: { label: 'Social Security numbers', example: '123-45-6789', allowable: true },
  US_ITIN: { label: 'Individual taxpayer IDs (ITIN)', example: '9XX-7X-XXXX', allowable: true },
  US_PASSPORT: { label: 'US passport numbers', example: 'a 9-character passport number', allowable: true },
  US_DRIVER_LICENSE: { label: "US driver's licence numbers", example: 'state licence number', allowable: true },
  MEDICAL_LICENSE: { label: 'Medical licence numbers', example: 'DEA or state medical licence', allowable: true },
  // Recognised by what surrounds it ("my password is …", "pw: …"), not by a
  // format — a password has none. The detector is stage 3's.
  PASSWORD: { label: 'Passwords', example: '"my password is …"', allowable: false },
};

/** The types a desk may allow. */
export const ALLOWABLE_PII = PII_ENTITIES.filter((e) => PII_INFO[e].allowable);

/** What makes a message decline, for a desk that allows `allowed`. */
export function blockedPii(allowed: readonly string[]): PiiEntity[] {
  return PII_ENTITIES.filter((e) => !(PII_INFO[e].allowable && allowed.includes(e)));
}

// ---------------------------------------------------------------------------
// The schema
// ---------------------------------------------------------------------------

export const INTAKE_TONES = [
  'friendly',
  'empathetic',
  'neutral',
  'concise',
  'formal',
  'technical',
  'custom',
] as const;
export type IntakeTone = (typeof INTAKE_TONES)[number];

/** Why a message was declined, in the order the most specific reason wins. */
export const DECLINE_REASONS = ['personalData', 'nsfw', 'outOfScope'] as const;
export type DeclineReason = (typeof DECLINE_REASONS)[number];

const line = (max: number) => z.string().trim().min(1).max(max);
const lines = (max: number, each: number) => z.array(line(each)).max(max);

/** A bare domain: `esassoc.com`, not `@esassoc.com` or a URL. */
const domain = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{3,253}$)([a-z0-9](-?[a-z0-9])*\.)+[a-z]{2,}$/, 'a domain, like esassoc.com');

/**
 * How the desk answers a thank-you: not at all, always with its own words,
 * or with a short reply a model writes to its guidance -- so the same
 * sentence does not arrive every time.
 */
export const WELCOME_MODES = ['off', 'fixed', 'guided'] as const;
export type WelcomeMode = (typeof WELCOME_MODES)[number];

/** Reply settings with every gap filled. On unless a desk switches it off. */
export function resolveReplies(r: IntakeSettings['replies'] | undefined): ResolvedIntake['replies'] {
  const mode: WelcomeMode = r?.welcome?.mode ?? 'guided';
  const own = r?.welcome?.text?.trim() ?? '';
  return {
    enabled: r?.enabled ?? true,
    closeWhenDone: r?.closeWhenDone ?? true,
    answerFollowUps: r?.answerFollowUps ?? false,
    welcome: {
      mode,
      text: own || (mode === 'fixed' ? DEFAULT_WELCOME_TEXT : DEFAULT_WELCOME_GUIDANCE),
      custom: Boolean(own),
    },
  };
}

export const intakeSettingsSchema = z
  .object({
    scope: z
      .object({
        /** One or two sentences: what this desk is. */
        summary: z.string().trim().max(500),
        handles: lines(30, 200),
        doesNotHandle: lines(30, 200),
        /** Real requests this desk handles, as a requester would write them. */
        examples: lines(20, 300),
      })
      .partial()
      .strict(),
    persona: z
      .object({
        tone: z.enum(INTAKE_TONES),
        /** The tone in the desk's own words, when `tone` is `custom`. */
        customTone: z.string().trim().max(500),
        /** How replies are signed, e.g. "ESA Tech Service Desk". */
        signOff: z.string().trim().max(200),
        /** Anything else about how this desk talks. Bounded on purpose. */
        notes: z.string().trim().max(1000),
      })
      .partial()
      .strict()
      .refine((p) => p.tone !== 'custom' || Boolean(p.customTone?.trim()), {
        message: 'describe the custom tone',
        path: ['customTone'],
      }),
    /** The reply sent for each decline. Empty or absent uses the default. */
    declines: z
      .object({
        personalData: z.string().trim().max(2000),
        nsfw: z.string().trim().max(2000),
        outOfScope: z.string().trim().max(2000),
      })
      .partial()
      .strict(),
    /**
     * Decline mail with strong profanity, slurs or abusive content. On unless
     * a desk switches it off: a desk like HR needs to see what was written,
     * exactly as it was written.
     */
    languageFilter: z.boolean(),
    /**
     * An email that asks for several things, some the knowledgebase answers
     * and some it does not: answer those parts now and keep the ticket open
     * for the rest. On unless a desk switches it off, when the whole email
     * waits for a person.
     */
    partialAnswers: z.boolean(),
    /** Always filed for a person, never answered, even with a strong match. */
    neverAnswer: lines(20, 200),
    /**
     * The reply sent when a message is filed for a person rather than
     * answered — so nobody is left wondering whether their email arrived.
     * `{ticket}` becomes the ticket reference. On unless switched off.
     */
    acknowledge: z
      .object({
        enabled: z.boolean(),
        text: z.string().trim().max(2000),
      })
      .partial()
      .strict(),
    /**
     * What an answered ticket records for fields the desk requires when a
     * ticket is closed (a resolution code, say), keyed by field. Without a
     * value the answer is still sent, but the ticket is left open for a
     * person to close.
     */
    answerFields: z.record(z.string().regex(/^[a-z][a-z0-9_]{0,63}$/), z.string().trim().min(1).max(200)),
    /**
     * What a reply to a RESOLVED or CLOSED ticket does.
     *
     * "Thanks, that worked" used to reopen the ticket -- or, on a closed one,
     * open a brand new ticket -- and an agent then closed it again by hand.
     * The reply is read first: thanks leaves it resolved, "go ahead and
     * close it" closes it, anything else behaves as it always did.
     */
    /**
     * What the assistant does with what this person has already asked.
     *
     *   joinOpen      a NEW email about a request they already have open is
     *                 added to that ticket, rather than opening a second one
     *   avoidRepeats  an article the assistant already sent them in the last
     *                 30 days is not sent again: it did not settle it, so a
     *                 person should look instead
     */
    history: z
      .object({
        joinOpen: z.boolean(),
        avoidRepeats: z.boolean(),
      })
      .partial()
      .strict(),
    replies: z
      .object({
        enabled: z.boolean(),
        closeWhenDone: z.boolean(),
        /**
         * A reply asking something new on a ticket the assistant answered:
         * let the assistant try once more, from a different article. Off
         * unless a desk switches it on.
         */
        answerFollowUps: z.boolean(),
        welcome: z
          .object({
            mode: z.enum(WELCOME_MODES),
            text: z.string().trim().max(1000),
          })
          .partial()
          .strict(),
      })
      .partial()
      .strict(),
    /** Mail from any other domain is rejected. `esassoc.com` was hardcoded in n8n. */
    allowedSenderDomains: z.array(domain).min(1).max(20),
    /**
     * Personal data the assistant may still file a ticket with. Absent or
     * empty: none — any of it declines. Card numbers and passwords are
     * refused here.
     */
    allowedPii: z
      .array(z.enum(PII_ENTITIES))
      .max(PII_ENTITIES.length)
      .refine((xs) => xs.every((x) => PII_INFO[x].allowable), {
        message: 'payment card numbers and passwords cannot be allowed',
      }),
  })
  .partial()
  .strict();
export type IntakeSettings = z.infer<typeof intakeSettingsSchema>;

/** The desk's own mailbox reply to a thank-you, once per resolution. */
export const DEFAULT_WELCOME_TEXT =
  'Glad that sorted it. If anything else comes up, just reply to this message and we will pick it up.';

/** What the model is told when the desk has not written its own guidance. */
export const DEFAULT_WELCOME_GUIDANCE =
  'One or two short sentences. Say you are glad it is sorted and that they can reply to this message if anything else comes up. Do not ask a question.';

// ---------------------------------------------------------------------------
// Defaults and resolution
// ---------------------------------------------------------------------------

export interface ResolvedIntake {
  scope: { summary: string; handles: string[]; doesNotHandle: string[]; examples: string[] };
  persona: { tone: IntakeTone; customTone: string; signOff: string; notes: string };
  /** Every reason filled: the tenant's wording, or the default. */
  declines: Record<DeclineReason, string>;
  /** Which declines are the tenant's own wording, for the screen. */
  customDeclines: Record<DeclineReason, boolean>;
  /** Whether inappropriate language is declined. */
  languageFilter: boolean;
  /** Whether the parts the knowledgebase covers are answered when others are not. */
  partialAnswers: boolean;
  neverAnswer: string[];
  acknowledge: { enabled: boolean; text: string; custom: boolean };
  history: { joinOpen: boolean; avoidRepeats: boolean };
  replies: {
    enabled: boolean;
    closeWhenDone: boolean;
    /** Answer one follow-up question on an assistant-answered ticket. */
    answerFollowUps: boolean;
    /** `text` is the message itself for `fixed`, guidance for `guided`. */
    welcome: { mode: WelcomeMode; text: string; custom: boolean };
  };
  /** Field values an answered ticket is closed with. */
  answerFields: Record<string, string>;
  allowedSenderDomains: string[];
  /** Allowed types, de-duplicated. */
  allowedPii: PiiEntity[];
  /** What declines — everything not allowed. */
  blockedPii: PiiEntity[];
}

export const DEFAULT_SENDER_DOMAINS = ['esassoc.com'];

/**
 * The default decline wording, per reason. Carried over from the n8n desk's
 * (`guardrail_decline.js`), where each was written after the single generic
 * decline told someone reporting a real computer fault that the desk handles
 * computer problems — which reads as broken. The specific reason is what lets
 * a person fix their message and send it again.
 */
export function defaultDecline(reason: DeclineReason, desk: string, handles: readonly string[]): string {
  const opening = 'Thanks for getting in touch.';
  switch (reason) {
    case 'personalData':
      return [
        opening,
        'It looks like this message contains {found}. I have not opened a ticket and have not passed ' +
          'the message on, so it does not end up sitting in a ticket where it is not needed.',
        `Send the request again without it and the ${desk} will pick it up. If that is what the problem ` +
          'is about, say so without quoting it and someone will get in touch.',
      ].join('\n\n');
    case 'nsfw':
      return [
        opening,
        'This message contains language that should not go onto a ticket, so I have not opened one and ' +
          `nothing has been recorded. A ticket is read by the ${desk} and stays on the record, which is ` +
          'why it is kept clean.',
        `Send the request again without it and it will go straight to the ${desk}.`,
      ].join('\n\n');
    case 'outOfScope':
      return [
        opening,
        handles.length > 0
          ? `The ${desk} covers ${joinList(handles)}. This does not look like one of those, so I have not opened a ticket.`
          : `This does not look like something the ${desk} handles, so I have not opened a ticket.`,
        `If I have read this wrong, reply with a little more detail and it will go to the ${desk}.`,
      ].join('\n\n');
  }
}

/** The default acknowledgement. `{ticket}` is filled in when it is sent. */
export function defaultAcknowledgement(desk: string): string {
  return [
    'Thanks for getting in touch.',
    `We have logged this as {ticket} and the ${desk} will pick it up. There is nothing more you need to do — reply to this message if you want to add anything.`,
  ].join('\n\n');
}

/**
 * Which acknowledgement a ticket gets: its request type's own (078), when it
 * has one, else the desk's. The type is matched by key or label.
 */
export function acknowledgementFor(
  deskText: string,
  requestType: string | null,
  types: readonly { key: string; label: string; acknowledgement?: string | null }[],
): string {
  const t = requestType ? types.find((x) => x.key === requestType || x.label === requestType) : undefined;
  return t?.acknowledgement?.trim() || deskText;
}

/** An acknowledgement with its ticket reference filled in. */
export function fillAcknowledgement(text: string, ticketRef: string): string {
  return text.includes('{ticket}') ? text.split('{ticket}').join(ticketRef) : `${text}\n\n(${ticketRef})`;
}

function joinList(items: readonly string[]): string {
  const xs = items.map((s) => s.replace(/[.;]\s*$/, ''));
  if (xs.length <= 1) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/**
 * A tenant's intake settings with every gap filled.
 *
 * Tolerant of anything: a row written before a field existed, or by hand,
 * resolves to the defaults for what it lacks rather than failing — the
 * schema is enforced on the way in, not assumed on the way out.
 */
export function resolveIntake(raw: unknown, desk: string): ResolvedIntake {
  const parsed = intakeSettingsSchema.safeParse(raw ?? {});
  const s: IntakeSettings = parsed.success ? parsed.data : {};
  const handles = s.scope?.handles ?? [];
  const declines = {} as Record<DeclineReason, string>;
  const customDeclines = {} as Record<DeclineReason, boolean>;
  for (const r of DECLINE_REASONS) {
    const own = s.declines?.[r]?.trim();
    customDeclines[r] = Boolean(own);
    declines[r] = own || defaultDecline(r, desk, handles);
  }
  return {
    scope: {
      summary: s.scope?.summary ?? '',
      handles,
      doesNotHandle: s.scope?.doesNotHandle ?? [],
      examples: s.scope?.examples ?? [],
    },
    persona: {
      tone: s.persona?.tone ?? 'friendly',
      customTone: s.persona?.customTone ?? '',
      signOff: s.persona?.signOff ?? '',
      notes: s.persona?.notes ?? '',
    },
    declines,
    customDeclines,
    languageFilter: s.languageFilter ?? true,
    partialAnswers: s.partialAnswers ?? true,
    neverAnswer: s.neverAnswer ?? [],
    acknowledge: {
      enabled: s.acknowledge?.enabled ?? true,
      text: s.acknowledge?.text?.trim() || defaultAcknowledgement(desk),
      custom: Boolean(s.acknowledge?.text?.trim()),
    },
    history: {
      joinOpen: s.history?.joinOpen ?? true,
      avoidRepeats: s.history?.avoidRepeats ?? true,
    },
    replies: resolveReplies(s.replies),
    answerFields: { ...(s.answerFields ?? {}) },
    allowedSenderDomains: s.allowedSenderDomains ?? DEFAULT_SENDER_DOMAINS,
    allowedPii: [...new Set(s.allowedPii ?? [])],
    blockedPii: blockedPii(s.allowedPii ?? []),
  };
}

// ---------------------------------------------------------------------------
// The tenant's part of the prompt
// ---------------------------------------------------------------------------

/** What each tone tells the model. `custom` is the desk's own words instead. */
export const TONE_TEXT: Record<Exclude<IntakeTone, 'custom'>, string> = {
  friendly: 'Warm and plain. Short sentences. First names are fine.',
  empathetic: 'Kind and patient. Acknowledge the frustration before the fix. Never blame the person.',
  neutral: 'Plain and direct. Polite, not chatty.',
  concise: 'As short as possible. The answer first, then only the steps needed. No pleasantries.',
  formal: 'Courteous and formal. No first names, no contractions.',
  technical: 'Precise and technical. Exact names of settings and menus; assume a capable reader.',
};

function toneText(p: ResolvedIntake['persona']): string {
  if (p.tone === 'custom') return p.customTone.trim() || TONE_TEXT.friendly;
  return TONE_TEXT[p.tone];
}

/**
 * The tenant sections of the assistant's prompt, as text.
 *
 * Only the TENANT'S part. The platform base is prepended by the agent
 * (stage 3) and is not configurable; this is what Configuration → Intake
 * previews, so an admin sees exactly what their settings say to the model.
 * An empty section is omitted rather than rendered as a heading over nothing.
 */
export function renderIntakeSections(r: ResolvedIntake, desk: string): string {
  const out: string[] = [];
  const bullets = (xs: readonly string[]) => xs.map((x) => `- ${x}`).join('\n');

  const scope: string[] = [];
  if (r.scope.summary) scope.push(r.scope.summary);
  if (r.scope.handles.length) scope.push(`This desk handles:\n${bullets(r.scope.handles)}`);
  // The NOT list never overrules the handles list: a request that fits both
  // is the desk's. Said here as well as in the screening rules, so the
  // answering model reads it the same way.
  if (r.scope.doesNotHandle.length) {
    scope.push(
      `This desk does NOT handle:\n${bullets(r.scope.doesNotHandle)}` +
        (r.scope.handles.length ? '\n(If a request fits both lists, this desk handles it.)' : ''),
    );
  }
  if (r.scope.examples.length) scope.push(`Examples of requests this desk handles:\n${bullets(r.scope.examples)}`);
  out.push(`## About the ${desk}\n\n${scope.length ? scope.join('\n\n') : '(No scope written yet.)'}`);

  const voice = [`Tone: ${toneText(r.persona)}`];
  if (r.persona.signOff) voice.push(`Sign replies: ${r.persona.signOff}`);
  if (r.persona.notes) voice.push(r.persona.notes);
  out.push(`## How this desk writes\n\n${voice.join('\n')}`);

  if (r.neverAnswer.length) {
    out.push(
      `## Always file for a person — never answer, even with a strong match\n\n${bullets(r.neverAnswer)}`,
    );
  }
  return out.join('\n\n');
}
